import { ContractorBriefDto, ContractorDto, ContractorOfEventDto, ContractorType } from '../services/api/sharedTypes/Contractor'

export const getEmptyContractorObject = (): ContractorDto => {
    return {
        id: '0',
        name: '',
        lastName: '',
        note: '',
        canBeAssigned: true,
        type: ContractorType.Brigadier,
        createdAt: new Date(),
        lastModifiedAt: new Date(),
        phones: [],
        emails: [],
    }
}

export const convertContractorBriefDtoToContractorOfEventDto = (contractor: ContractorBriefDto): ContractorOfEventDto => {
    return {
        contractorId: contractor.id,
        contractorName: contractor.name + ' ' + contractor.lastName,
        contractorType: contractor.type,
        isConfirmed: false,
        costs: 0,
        gotPaid: false,
        hasResponded: false,
        isLeader: false,
    }
}

/**
 * Function to get contractor name and last name and all events they are part of in a specific time period.
 * If there is no event, contractor is not in the list of contractors or there are no assigned events, returns null.
 * @param contractorId Id of the contractor to check
 * @param eventId EventId to check and find if the contractor is assigned to that event
 * @param eventStartsAt Event startsAt to check if the contractor is assigned to that event
 * @param eventEndsAt Event endsAt to check if the contractor is assigned to that event
 * @param contractorList List of all contractors to find the contractor in by contractorId
 * @returns Contractor name and last name and all events they are part of or null if there is no event, contractor is not in the list of contractors or there are no assigned events
 * @example
 * findNameOfContractorAssignedToDifferentEventById('08da899d-2935-45a8-8435-14eb12704040', event, event.startsAt, event.endsAt, contractors)
 * // returns { name: 'John Doe', events: [event1, event2] }
 */
export const findNameOfContractorAssignedToDifferentEventById = (
    contractorId: string,
    eventId: string,
    eventStartsAt: Date,
    eventEndsAt: Date,
    contractorList: ContractorBriefDto[]
) => {
    if (!contractorList) return null
    const contractor = contractorList.find((c) => c.id === contractorId)
    if (!contractor) return null
    if (contractor.name === 'Null' || !contractor.id) return null

    const assignedEvents = contractor.assignedEvents
        .filter((e) => e.eventId !== eventId)
        .filter((e) => e.isConfirmed)
        .filter((e) => {
            if (!e.startsAt && !e.endsAt) return false

            const startsAt = new Date(e.startsAt)
            const endsAt = new Date(e.endsAt)

            if (startsAt >= new Date(eventStartsAt) && startsAt <= new Date(eventEndsAt)) return true

            if (endsAt >= new Date(eventStartsAt) && endsAt <= new Date(eventEndsAt)) return true

            if (startsAt <= new Date(eventStartsAt) && endsAt >= new Date(eventEndsAt)) return true

            return false
        })
    if (assignedEvents.length === 0) return null
    return {
        name: contractor.name + ' ' + contractor.lastName,
        events: assignedEvents,
    }
}

export enum ContractorTab {
    All = 'all',
    Brigadier = 'brigadier',
    Carrier = 'carrier',
}

export const getContractorTab = (tab: string) => {
    if (tab === ContractorTab.All) return ContractorTab.All
    if (tab === ContractorTab.Brigadier) return ContractorTab.Brigadier
    if (tab === ContractorTab.Carrier) return ContractorTab.Carrier
    return ContractorTab.All
}

export const getContractorTabNumber = (tab: ContractorTab) => {
    if (tab === ContractorTab.All) return 0
    if (tab === ContractorTab.Brigadier) return 1
    if (tab === ContractorTab.Carrier) return 2
    return 0
}

export const convertNumberToContractorTab = (data: number) => {
    if (data === 0) return ContractorTab.All
    if (data === 1) return ContractorTab.Brigadier
    if (data === 2) return ContractorTab.Carrier
    return ContractorTab.All
}

export const convertContractorOfEventDtoToContractorBriefDto = (contractor: ContractorOfEventDto): ContractorBriefDto => {
    return {
        assignedEvents: [],
        canBeAssigned: true,
        createdAt: new Date(),
        emails: [],
        id: contractor.contractorId ?? '',
        lastName: '',
        lastModifiedAt: new Date(),
        name: contractor.contractorName,
        note: '',
        phones: [],
        type: contractor.contractorType,
        emailCount: 0,
        phoneCount: 0,
        eventCount: 0,
        paidInTotal: 0,
    }
}
